import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDateTime } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ScanLine, ArrowRight, Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const ScanHistoryReport = ({ days }) => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState("all");
  const [date, setDate] = useState("");

  useEffect(() => {
    const p = { days };
    if (staff !== "all") p.user_id = staff;
    if (date) p.date = date;
    api.get("/reports/scans", { params: p }).then((r) => setData(r.data));
  }, [days, staff, date]);

  const [staffOptions, setStaffOptions] = useState([]);
  useEffect(() => { api.get("/reports/scans", { params: { days: 365 } }).then((r) => setStaffOptions(r.data.by_staff)); }, []);

  if (!data) return null;
  const download = async (fmt) => {
    const p = { days, fmt };
    if (staff !== "all") p.user_id = staff;
    if (date) p.date = date;
    try {
      const r = await api.get("/reports/scans/export", { params: p, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `scan_history.${fmt}`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="scan-history-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display font-bold flex items-center gap-2"><ScanLine className="w-4 h-4" /> Scan History <Badge variant="secondary" className="font-mono" data-testid="scan-history-total">{data.total}</Badge></h2>
        <div className="flex gap-2 flex-wrap">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="scan-history-date" />
          <Select value={staff} onValueChange={setStaff}>
            <SelectTrigger className="w-48" data-testid="scan-history-staff"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              {staffOptions.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => download("xlsx")} disabled={!data.total} data-testid="scan-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
          <Button size="sm" variant="outline" onClick={() => download("csv")} disabled={!data.total} data-testid="scan-export-csv"><Download className="w-4 h-4 mr-1" /> CSV</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-mono uppercase text-muted-foreground mb-2">Scans per day</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data.by_day}><XAxis dataKey="date" fontSize={10} tickFormatter={(d) => d.slice(5)} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip /><Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-muted-foreground mb-2">Per staff</div>
          <div className="divide-y divide-border" data-testid="scan-history-staff-list">
            {data.by_staff.length === 0 && <div className="text-sm text-muted-foreground py-3">No scans in this period.</div>}
            {data.by_staff.map((s) => (
              <button key={s.user_id} onClick={() => setStaff(s.user_id)} className="w-full flex items-center justify-between py-2 text-sm hover:bg-muted/50 px-2 rounded text-left" data-testid={`scan-staff-${s.user_id}`}>
                <div><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.role}</div></div>
                <div className="text-right font-mono text-xs"><div className="font-bold text-base">{s.count}</div><div className="text-muted-foreground">{s.jobs} job(s)</div></div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground uppercase font-mono"><tr className="text-left border-b"><th className="py-2 pr-3">Time</th><th className="pr-3">Staff</th><th className="pr-3">Job</th><th className="pr-3">Change</th><th>Mode</th></tr></thead>
          <tbody data-testid="scan-history-rows">
            {data.rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60 hover:bg-muted/40 cursor-pointer" onClick={() => nav(`/jobs/${r.job_id}`)} data-testid={`scan-row-${r.id}`}>
                <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{formatDateTime(r.at)}</td>
                <td className="pr-3">{r.user_name} <span className="text-xs text-muted-foreground">({r.role})</span></td>
                <td className="pr-3"><span className="font-mono font-semibold">{r.job_number}</span> <span className="text-xs text-muted-foreground">{r.customer_name}</span></td>
                <td className="pr-3"><span className="inline-flex items-center gap-1"><StatusBadge status={r.from_status} /><ArrowRight className="w-3 h-3" /><StatusBadge status={r.to_status} /></span></td>
                <td><Badge variant="outline" className="text-[10px] uppercase">{r.mode}</Badge></td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No scan records.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
